import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  addDoc, collection, deleteDoc, doc, getDoc, orderBy, query, setDoc, updateDoc
} from 'firebase/firestore';
import { db, disableWorkspacePushNotifications, enableWorkspacePushNotifications, initAnalytics, isWorkspacePushSubscribed, watchAuthState } from './firebase';
import { actualizarModoWorkspace, wheniseeusAI, eliminarWorkspace, invitarAWorkspace } from './api';
import { joinVoiceChannel } from './voiceChannel';
import { uploadAttachment, uploadBlob, captureVideoFrameFromFile, captureStreamFrame, isStorageUrl } from './attachments';
import { subscribeWithRetry } from './firestoreRetry';
import { translate, getStoredLanguage, setStoredLanguage } from './i18n';
import { getStoredTheme, useTheme } from './theme';
import LanguageToggle from './LanguageToggle';
import './SubscribePage.css';
import './WorkspacesPage.css';
import './WorkspaceDetailPage.css';

const MAX_IMAGE_ATTACHMENTS = 4;
// Kept in sync by hand with backend/app/schemas.py's MAX_DOCUMENT_ATTACHMENTS
// (same limit src/App.js uses for the personal chat).
const MAX_DOCUMENT_ATTACHMENTS = 3;
const DOCUMENT_ACCEPT = '.pdf,.docx,.txt,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const screenShareSupported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

function useClickOutside(ref, enabled, setOpen) {
  useEffect(() => {
    if (!enabled) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, enabled, setOpen]);
}

// There's no separate "display name" field anywhere in this app - members
// are only ever known by the email they signed in/were invited with (see
// backend/app/services/workspaces.py). Deriving a human-looking name from
// the address's local part ("juan.carlos@x.com" -> "Juan Carlos") is the
// only name the AI (or the task list) has to work with.
function memberDisplayName(member) {
  const local = (member?.email || '').split('@')[0] || member?.uid || '';
  const pretty = local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
  return pretty || local;
}

// Nudges the AI toward shorter, structured, task-oriented replies while a
// workspace is in "work" mode, without needing a dedicated system-prompt
// parameter on the backend (ChatHistoryMessage only supports user/ai roles,
// see backend/app/schemas.py) - a synthetic leading turn is the lightest
// way to get there without a backend change. Telling it who's actually in
// the group, and the exact tag format to lead with, is what makes
// extract_task_tag (backend/app/services/workspaces.py) able to reliably
// turn a reply into an assigned task - the extraction itself now happens
// server-side, alongside writing the reply, not here (see handleSend).
function buildWorkModeHint(members) {
  const names = members.map(memberDisplayName).join(', ') || '(sin miembros)';
  return {
    role: 'user',
    text: 'Instrucciones para este chat grupal de trabajo: sé breve y estructurado. '
      + `Los miembros del grupo son: ${names}. `
      + 'Si tu respuesta (por ejemplo código) es específicamente para uno de ellos, empezá tu respuesta '
      + 'con "[TAREA:Nombre]" usando exactamente ese nombre, sin nada antes. Si no es para nadie en '
      + 'particular, no uses la etiqueta.'
  };
}

// Recent turns only - backend caps historial at 12 (see ChatRequest in
// backend/app/schemas.py); trimming here keeps the payload small regardless.
const MAX_HISTORY_MESSAGES = 12;
// Per-turn cap of the backend's ChatHistoryMessage.text (schemas.py).
const MAX_HISTORY_TEXT = 4000;

export default function WorkspaceDetailPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const [language, setLanguage] = useState(() => getStoredLanguage());
  const t = (key) => translate(language, key);
  useTheme(getStoredTheme());
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setStoredLanguage(lang);
  };

  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadError, setLoadError] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteNotice, setInviteNotice] = useState('');
  const [inviteError, setInviteError] = useState('');

  const [deletingWorkspace, setDeletingWorkspace] = useState(false);
  const [deleteWorkspaceError, setDeleteWorkspaceError] = useState('');
  const [changingMode, setChangingMode] = useState(false);

  const [files, setFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileUploadError, setFileUploadError] = useState('');
  const repoFileInputRef = useRef(null);

  // Shared by the file repository list and chat message attachments -
  // clicking an image/video opens it full-size here instead of just
  // navigating away; copiedKey briefly flags which "copy link" button was
  // just clicked so the click has visible feedback (the file's own url
  // works fine as the key since it's unique per attachment).
  const [previewFile, setPreviewFile] = useState(null);
  const [copiedKey, setCopiedKey] = useState('');

  // Reflects the real, persisted subscription state (Notification.permission
  // + a matching Firestore token doc - see isWorkspacePushSubscribed), not
  // just "did I successfully click enable this session". Without checking
  // on mount, the button always started dark/off on every reload even for
  // an already-subscribed browser, so the very first click there would
  // silently re-enable something that was already on instead of disabling
  // it as the (already-active-looking... except it wasn't) button implied.
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    isWorkspacePushSubscribed(user.uid).then((subscribed) => {
      if (!cancelled) setPushSubscribed(subscribed);
    });
    return () => { cancelled = true; };
  }, [user?.uid]);

  const handleTogglePushNotifications = async () => {
    if (!user?.uid || pushBusy) return;
    setPushBusy(true);
    try {
      if (pushSubscribed) {
        await disableWorkspacePushNotifications(user.uid);
        setPushSubscribed(false);
      } else {
        const token = await enableWorkspacePushNotifications(user.uid);
        setPushSubscribed(!!token);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setPushBusy(false);
    }
  };

  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  // Attachments + screen share on a group message - same upload pipeline as
  // src/App.js's personal chat (Storage under ia/{uid}/{conversationId}/...,
  // then the same imagenes/documentos fields on wheniseeusAI), except a
  // screen-share frame here also becomes a visible message attachment (see
  // handleSend) so the other members actually see what was shared, not just
  // the AI - the personal chat only sends it to the AI.
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [screenShareActive, setScreenShareActive] = useState(false);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const attachMenuRef = useRef(null);
  const screenStreamRef = useRef(null);
  const screenVideoRef = useRef(null);

  const [tasks, setTasks] = useState([]);
  const [taskFilter, setTaskFilter] = useState('all');

  // Mobile-only: below 760px the sidebar and chat can't sit side by side
  // (see WorkspaceDetailPage.css), so this toggles which pane is showing
  // instead of squeezing the sidebar into a fixed max-height with its own
  // scroll - that used to clip the invite button and left barely any room
  // for the chat composer. Unused above 760px, where CSS keeps both panes
  // visible regardless of this state.
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Voice channel - entirely separate from the chat/AI above (its own
  // peer-to-peer connections via src/voiceChannel.js), so joining or
  // talking never blocks or waits on a chat message or an AI reply.
  const voiceControllerRef = useRef(null);
  const [inVoice, setInVoice] = useState(false);
  const [voiceParticipants, setVoiceParticipants] = useState([]);
  const [voiceMuted, setVoiceMuted] = useState(false);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [voiceError, setVoiceError] = useState('');

  const chatEndRef = useRef(null);

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    const unsubscribeAuth = watchAuthState((nextUser) => {
      setUser(nextUser);
      if (!nextUser) navigate('/');
    });
    return unsubscribeAuth;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live workspace doc - reflects a mode change or a new member from
  // anywhere (another tab, another person) without a manual reload.
  // Waits for user.uid the same way WorkspacesPage's list query does: on a
  // hard refresh landing straight on this URL, Firebase Auth hasn't
  // restored the session yet, so subscribing immediately would hit the
  // rules' isSignedIn() check as signed-out and get a permission-denied.
  // See subscribeWithRetry above for why that guard alone isn't quite
  // enough and this still needs its own retry.
  useEffect(() => {
    if (!user?.uid) return;
    return subscribeWithRetry(
      () => doc(db, 'workspaces', workspaceId),
      (snap) => {
        if (!snap.exists()) {
          setLoadError(t('errLoadWorkspace'));
          return;
        }
        setWorkspace({ id: snap.id, ...snap.data() });
      },
      () => setLoadError(t('errLoadWorkspace'))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    return subscribeWithRetry(
      () => collection(db, 'workspaces', workspaceId, 'members'),
      (snapshot) => setMembers(snapshot.docs.map((d) => d.data())),
      () => {}
    );
  }, [workspaceId, user?.uid]);

  // The group chat lives at /chats/{workspaceId} (one chat per workspace,
  // its id doubling as the workspace's id - see firestore.rules) with its
  // messages in a subcollection rather than one array field, so concurrent
  // senders never overwrite each other's messages (the original single-user
  // /chats/{id}.messages array model can't do that safely for a group).
  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      const chatRef = doc(db, 'chats', workspaceId);
      // Same permission-denied race as subscribeWithRetry, but for a
      // one-shot getDoc rather than a listener - one retry here too.
      let chatSnap;
      try {
        chatSnap = await getDoc(chatRef);
      } catch (raceError) {
        if (raceError?.code !== 'permission-denied') throw raceError;
        await new Promise((resolve) => setTimeout(resolve, 400));
        chatSnap = await getDoc(chatRef);
      }
      if (!chatSnap.exists()) {
        await setDoc(chatRef, {
          userId: user?.uid,
          workspaceId,
          title: workspace?.name || 'Group chat',
          createdAt: Date.now(),
          updatedAt: Date.now()
        }, { merge: true });
      }
    })().catch(() => {});
    // Runs once workspace/user are known - not on every workspace field
    // change, so this doesn't refire on a mode toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, !!user, !!workspace]);

  useEffect(() => {
    if (!user?.uid) return;
    const messagesQuery = query(
      collection(db, 'chats', workspaceId, 'messages'),
      orderBy('createdAt', 'asc')
    );
    return subscribeWithRetry(
      () => messagesQuery,
      (snapshot) => setMessages(snapshot.docs.map((d) => {
        const data = d.data();
        const attachments = Array.isArray(data.attachments) ? data.attachments.filter((a) => isStorageUrl(a?.url)) : undefined;
        return { id: d.id, ...data, attachments };
      })),
      () => {}
    );
  }, [workspaceId, user?.uid]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!user?.uid) return;
    const tasksQuery = query(
      collection(db, 'workspaces', workspaceId, 'tasks'),
      orderBy('createdAt', 'desc')
    );
    return subscribeWithRetry(
      () => tasksQuery,
      (snapshot) => setTasks(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => {}
    );
  }, [workspaceId, user?.uid]);

  // Shared file library - separate from chat message attachments: a file
  // uploaded here isn't tied to any particular message, it just lives in
  // the group's own list for anyone to browse/download later.
  useEffect(() => {
    if (!user?.uid) return;
    const filesQuery = query(
      collection(db, 'workspaces', workspaceId, 'files'),
      orderBy('createdAt', 'desc')
    );
    return subscribeWithRetry(
      () => filesQuery,
      (snapshot) => setFiles(snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((file) => isStorageUrl(file.url))),
      () => {}
    );
  }, [workspaceId, user?.uid]);

  const myMembership = useMemo(
    () => members.find((m) => m.uid === user?.uid) || null,
    [members, user?.uid]
  );
  const isAdmin = myMembership?.role === 'admin';
  const visibleTasks = taskFilter === 'mine'
    ? tasks.filter((task) => task.assignedToUid === user?.uid)
    : tasks;

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim() || inviting) return;
    setInviting(true);
    setInviteError('');
    setInviteNotice('');
    try {
      const result = await invitarAWorkspace({ workspaceId, email: inviteEmail.trim() });
      setInviteEmail('');
      setInviteNotice(`${t(result.emailSent === false ? 'inviteShareCodePrefix' : 'inviteSentPrefix')} ${result.code}`);
    } catch {
      setInviteError(t('errInvite'));
    } finally {
      setInviting(false);
    }
  };

  const handleModeToggle = async (mode) => {
    if (!isAdmin || !workspace || workspace.mode === mode || changingMode) return;
    setChangingMode(true);
    try {
      // A cold backend can take a while to answer this (see api.js's
      // request() retry loop) - staying disabled for the whole attempt
      // stops a slow first click from turning into a pile of repeated
      // clicks once it's finally warm.
      await actualizarModoWorkspace({ workspaceId, mode });
    } catch {
      // The realtime listener will just keep showing the last confirmed
      // mode if this fails - no separate error UI needed for a toggle.
    } finally {
      setChangingMode(false);
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!isAdmin || deletingWorkspace) return;
    if (!window.confirm(t('confirmDeleteWorkspace'))) return;
    setDeletingWorkspace(true);
    setDeleteWorkspaceError('');
    try {
      await eliminarWorkspace({ workspaceId });
      navigate('/workspaces');
    } catch {
      setDeleteWorkspaceError(t('errDeleteWorkspace'));
      setDeletingWorkspace(false);
    }
  };

  const attachFiles = (files, kind) => {
    if (files.length === 0) return;

    setPendingAttachments((prev) => {
      const max = kind === 'document' ? MAX_DOCUMENT_ATTACHMENTS : MAX_IMAGE_ATTACHMENTS;
      const countOfKind = prev.filter((a) => (kind === 'document' ? a.kind === 'document' : a.kind !== 'document')).length;
      const room = Math.max(max - countOfKind, 0);
      const accepted = files.slice(0, room).map((file) => ({
        id: `${Date.now()}-${Math.random()}`,
        file,
        kind,
        previewUrl: kind === 'document' ? null : URL.createObjectURL(file)
      }));
      return [...prev, ...accepted];
    });
  };

  const handleAttachFiles = (e, kind) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    attachFiles(files, kind);
  };

  const removePendingAttachment = (id) => {
    setPendingAttachments((prev) => {
      const target = prev.find((a) => a.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  useClickOutside(attachMenuRef, attachMenuOpen, setAttachMenuOpen);

  const toggleScreenShare = async () => {
    if (screenShareActive) {
      screenStreamRef.current?.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
      setScreenShareActive(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      screenStreamRef.current = stream;
      if (screenVideoRef.current) {
        screenVideoRef.current.srcObject = stream;
        await screenVideoRef.current.play().catch(() => {});
      }
      stream.getVideoTracks()[0].addEventListener('ended', () => {
        screenStreamRef.current = null;
        setScreenShareActive(false);
      });
      setScreenShareActive(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    const hasAttachments = pendingAttachments.length > 0;
    if ((!text && !hasAttachments && !screenShareActive) || sending || !user) return;
    const messageText = text || t('describeAttachmentFallback');
    const attachmentsToUpload = pendingAttachments;
    setSending(true);
    setSendError('');
    setDraft('');
    setPendingAttachments([]);

    try {
      const imagenes = [];
      const documentos = [];
      const displayAttachments = [];

      for (const item of attachmentsToUpload) {
        if (item.kind === 'video') {
          const [fileUrl, frameBlob] = await Promise.all([
            uploadAttachment(item.file, user.uid, workspaceId),
            captureVideoFrameFromFile(item.file)
          ]);
          displayAttachments.push({ type: 'video', url: fileUrl, name: item.file.name });
          imagenes.push(await uploadBlob(frameBlob, user.uid, workspaceId, `${item.file.name}-frame.jpg`));
        } else if (item.kind === 'document') {
          const url = await uploadAttachment(item.file, user.uid, workspaceId);
          documentos.push(url);
          displayAttachments.push({ type: 'document', url, name: item.file.name });
        } else {
          const url = await uploadAttachment(item.file, user.uid, workspaceId);
          imagenes.push(url);
          displayAttachments.push({ type: 'image', url, name: item.file.name });
        }
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      }

      // Unlike src/App.js's personal chat (where a screen-share frame only
      // ever goes to the AI), this also lands in displayAttachments so the
      // other workspace members actually see what was shared, not just the
      // assistant.
      if (screenShareActive && screenVideoRef.current) {
        try {
          const frameBlob = await captureStreamFrame(screenVideoRef.current);
          const frameUrl = await uploadBlob(frameBlob, user.uid, workspaceId, 'screen-frame.jpg');
          imagenes.push(frameUrl);
          displayAttachments.push({ type: 'image', url: frameUrl, name: t('screenSharing') });
        } catch (captureError) {
          console.error(captureError);
        }
      }

      await addDoc(collection(db, 'chats', workspaceId, 'messages'), {
        workspaceId,
        role: 'user',
        authorUid: user.uid,
        authorName: user.email || t('youBadge'),
        text: messageText,
        createdAt: Date.now(),
        ...(displayAttachments.length > 0 ? { attachments: displayAttachments } : {})
      });

      const recentHistory = messages
        .slice(-MAX_HISTORY_MESSAGES)
        .map((m) => ({ role: m.role, text: String(m.text || '').slice(0, MAX_HISTORY_TEXT) }))
        .filter((m) => m.text);
      const isWorkMode = workspace?.mode === 'work';
      // The hint counts toward the backend's 12-turn historial cap, so it
      // takes the oldest turn's slot rather than pushing the total to 13.
      const historial = isWorkMode
        ? [buildWorkModeHint(members), ...recentHistory.slice(1 - MAX_HISTORY_MESSAGES)]
        : recentHistory;

      // The AI's reply - and, in work mode, any [TAREA:Nombre] task it
      // creates - is written by the backend itself (see backend/app/
      // routers/chat.py + services/workspaces.py's record_group_ai_message)
      // once it actually finishes generating, not by this client. Firestore
      // rules refuse a client-written role=='ai' message outright, so
      // there's nothing to addDoc here - the realtime listener above picks
      // the new message up once the backend writes it, same as it does for
      // every other member.
      await wheniseeusAI({
        mensaje: messageText,
        historial,
        imagenes: imagenes.slice(0, MAX_IMAGE_ATTACHMENTS),
        documentos: documentos.slice(0, MAX_DOCUMENT_ATTACHMENTS),
        workspaceId
      });
    } catch {
      setSendError(t('errSendMessage'));
    } finally {
      setSending(false);
    }
  };

  // Manual fallback for the AI's own [TAREA:Nombre] tagging (extracted
  // server-side, see backend/app/services/workspaces.py's
  // extract_task_tag) - a local model won't always follow the convention,
  // so any message (from a person or the AI) can be turned into a task by
  // hand via the assign dropdown under it.
  const handleAssignTask = async (message, targetUid) => {
    const targetMember = members.find((m) => m.uid === targetUid);
    if (!targetMember || !user) return;
    try {
      await addDoc(collection(db, 'workspaces', workspaceId, 'tasks'), {
        text: message.text,
        assignedToUid: targetMember.uid,
        assignedToName: memberDisplayName(targetMember),
        createdByUid: user.uid,
        sourceMessageId: message.id,
        status: 'pending',
        createdAt: Date.now(),
        updatedAt: Date.now()
      });
    } catch {
      // Best-effort, same as the mode toggle - no dedicated error UI for a
      // single assign action failing.
    }
  };

  const handleToggleTask = async (task) => {
    try {
      await updateDoc(doc(db, 'workspaces', workspaceId, 'tasks', task.id), {
        status: task.status === 'done' ? 'pending' : 'done',
        updatedAt: Date.now()
      });
    } catch {
      // Realtime listener just keeps showing the last confirmed status.
    }
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || uploadingFile || !user) return;
    setUploadingFile(true);
    setFileUploadError('');
    try {
      const url = await uploadAttachment(file, user.uid, workspaceId);
      const type = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'document';
      await addDoc(collection(db, 'workspaces', workspaceId, 'files'), {
        type,
        url,
        name: file.name,
        uploadedByUid: user.uid,
        uploadedByName: user.email || t('youBadge'),
        createdAt: Date.now()
      });
    } catch {
      setFileUploadError(t('errUploadFile'));
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm(t('confirmDeleteFile'))) return;
    try {
      await deleteDoc(doc(db, 'workspaces', workspaceId, 'files', file.id));
    } catch {
      // Realtime listener just keeps showing it if this fails.
    }
  };

  const handleCopyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(url);
      setTimeout(() => setCopiedKey((current) => (current === url ? '' : current)), 1500);
    } catch (error) {
      console.error(error);
    }
  };

  // Documents (pdf/docx/txt...) have nothing worth rendering in-app, so
  // "view" just opens them in a new tab like before; images/videos get the
  // lightbox below instead.
  const openFilePreview = (file) => {
    if (file.type === 'document') {
      window.open(file.url, '_blank', 'noopener,noreferrer');
    } else {
      setPreviewFile(file);
    }
  };

  // Shared by the file repository list and chat message attachments.
  const renderFileActions = (file) => (
    <div className="file-actions">
      <button type="button" className="file-action-btn" onClick={() => openFilePreview(file)} aria-label={t('viewFileAria')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>
      <button type="button" className="file-action-btn" onClick={() => handleCopyLink(file.url)} aria-label={t('copyLinkAria')}>
        {copiedKey === file.url ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
          </svg>
        )}
      </button>
      <a href={file.url} download={file.name} target="_blank" rel="noreferrer" className="file-action-btn" aria-label={t('downloadFileAria')}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 15, height: 15 }}>
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      </a>
    </div>
  );

  const handleJoinVoice = async () => {
    if (!user || inVoice) return;
    setVoiceError('');
    const controller = await joinVoiceChannel({
      workspaceId,
      uid: user.uid,
      displayName: memberDisplayName({ email: user.email, uid: user.uid }),
      onParticipantsChange: setVoiceParticipants,
      onRemoteStream: (remoteUid, stream) => {
        setRemoteStreams((prev) => {
          const next = { ...prev };
          if (stream) next[remoteUid] = stream; else delete next[remoteUid];
          return next;
        });
      },
      onError: () => setVoiceError(t('errVoiceMic'))
    });
    if (controller) {
      voiceControllerRef.current = controller;
      setInVoice(true);
    }
  };

  const handleLeaveVoice = async () => {
    await voiceControllerRef.current?.leave();
    voiceControllerRef.current = null;
    setInVoice(false);
    setVoiceParticipants([]);
    setRemoteStreams({});
    setVoiceMuted(false);
  };

  const handleToggleMute = () => {
    if (!voiceControllerRef.current) return;
    const next = !voiceMuted;
    voiceControllerRef.current.setMuted(next);
    setVoiceMuted(next);
  };

  // Leaving the page (navigating elsewhere) while still in a call shouldn't
  // leave a dangling presence doc / open peer connections behind.
  useEffect(() => () => { voiceControllerRef.current?.leave(); }, []);

  // Same for a still-active screen share - navigating away shouldn't leave
  // the browser's "you're sharing your screen" indicator on forever.
  useEffect(() => () => { screenStreamRef.current?.getTracks().forEach((track) => track.stop()); }, []);

  if (loadError) {
    return (
      <div className="sub-page">
        <main className="sub-page-main workspaces-main">
          <p className="workspace-error">{loadError}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="sub-page">
      <header className="sub-page-header">
        <div className="sub-page-titlebar">
          <div className="sub-page-titlebar-start">
            <button
              className="sub-back-icon-btn"
              type="button"
              onClick={() => navigate('/workspaces')}
              aria-label={t('backToWorkspaces')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="sub-page-title">{workspace?.name || '...'}</h1>
          </div>
          <div className="workspace-header-actions">
            <button
              type="button"
              className={`workspace-push-toggle${pushSubscribed ? ' active' : ''}`}
              onClick={handleTogglePushNotifications}
              disabled={pushBusy}
              aria-label={t(pushSubscribed ? 'disableNotificationsAria' : 'enableNotificationsAria')}
              aria-pressed={pushSubscribed}
              title={t(pushSubscribed ? 'disableNotificationsAria' : 'enableNotificationsAria')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                {!pushSubscribed && <path d="M1 1l22 22" />}
              </svg>
            </button>
            <button
              type="button"
              className={`workspace-sidebar-toggle${sidebarOpen ? ' active' : ''}`}
              onClick={() => setSidebarOpen((open) => !open)}
              aria-label={t('toggleWorkspaceMenu')}
              aria-expanded={sidebarOpen}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <LanguageToggle language={language} onChange={handleLanguageChange} />
          </div>
        </div>
      </header>

      <main className="workspace-detail-main">
        <aside className={`workspace-sidebar${sidebarOpen ? ' open' : ''}`}>
          <div className={`workspace-mode-toggle${changingMode ? ' busy' : ''}`}>
            <button
              type="button"
              className={`workspace-mode-btn${workspace?.mode === 'chat' ? ' active' : ''}`}
              disabled={!isAdmin || changingMode}
              onClick={() => handleModeToggle('chat')}
            >
              {t('chatModeLabel')}
            </button>
            <button
              type="button"
              className={`workspace-mode-btn${workspace?.mode === 'work' ? ' active' : ''}`}
              disabled={!isAdmin || changingMode}
              onClick={() => handleModeToggle('work')}
            >
              {t('workModeLabel')}
            </button>
          </div>

          <div className="workspace-voice">
            <h3>{t('voiceChannelTitle')}</h3>
            {voiceError && <p className="workspace-error">{voiceError}</p>}
            {!inVoice ? (
              <button type="button" className="workspace-submit-btn" onClick={handleJoinVoice}>
                {t('joinVoice')}
              </button>
            ) : (
              <div className="workspace-voice-active">
                <ul className="workspace-voice-participant-list">
                  <li className="workspace-voice-participant">
                    <span className="workspace-voice-dot" />
                    {t('youBadge')}
                    {voiceMuted && <span className="workspace-role-badge">{t('mutedBadge')}</span>}
                  </li>
                  {voiceParticipants.map((participant) => (
                    <li key={participant.uid} className="workspace-voice-participant">
                      <span className="workspace-voice-dot" />
                      {participant.displayName}
                      {participant.mutedByUser && <span className="workspace-role-badge">{t('mutedBadge')}</span>}
                    </li>
                  ))}
                </ul>
                <div className="workspace-voice-controls">
                  <button type="button" className="workspace-action-btn" onClick={handleToggleMute}>
                    {voiceMuted ? t('unmuteLabel') : t('muteLabel')}
                  </button>
                  <button type="button" className="workspace-danger-btn" onClick={handleLeaveVoice}>
                    {t('leaveVoice')}
                  </button>
                </div>
              </div>
            )}
            {Object.entries(remoteStreams).map(([remoteUid, stream]) => (
              <audio key={remoteUid} autoPlay ref={(el) => { if (el) el.srcObject = stream; }} />
            ))}
          </div>

          <div className="workspace-members">
            <h3>{t('membersTitle')}</h3>
            <ul>
              {members.map((member) => (
                <li key={member.uid}>
                  <span>{memberDisplayName(member)}</span>
                  {member.role === 'admin' && <span className="workspace-role-badge">{t('adminBadge')}</span>}
                </li>
              ))}
            </ul>
          </div>

          <div className="workspace-tasks">
            <div className="workspace-tasks-header">
              <h3>{t('tasksTitle')}</h3>
              <div className="workspace-task-filter">
                <button
                  type="button"
                  className={taskFilter === 'all' ? 'active' : ''}
                  onClick={() => setTaskFilter('all')}
                >
                  {t('taskFilterAll')}
                </button>
                <button
                  type="button"
                  className={taskFilter === 'mine' ? 'active' : ''}
                  onClick={() => setTaskFilter('mine')}
                >
                  {t('taskFilterMine')}
                </button>
              </div>
            </div>
            {visibleTasks.length === 0 && <p className="workspace-empty-small">{t('noTasks')}</p>}
            <ul className="workspace-task-list">
              {visibleTasks.map((task) => (
                <li key={task.id} className={`workspace-task-item${task.status === 'done' ? ' done' : ''}`}>
                  <input
                    type="checkbox"
                    checked={task.status === 'done'}
                    onChange={() => handleToggleTask(task)}
                  />
                  <div className="workspace-task-body">
                    <span className="workspace-task-assignee">{task.assignedToName}</span>
                    <p>{task.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="workspace-files">
            <div className="workspace-files-header">
              <h3>{t('filesTitle')}</h3>
              <input type="file" hidden ref={repoFileInputRef} onChange={handleUploadFile} />
              <button
                type="button"
                className="workspace-action-btn"
                onClick={() => repoFileInputRef.current?.click()}
                disabled={uploadingFile}
              >
                {uploadingFile ? t('uploading') : t('uploadFile')}
              </button>
            </div>
            {fileUploadError && <p className="workspace-error">{fileUploadError}</p>}
            {files.length === 0 && <p className="workspace-empty-small">{t('noFiles')}</p>}
            <ul className="workspace-file-list">
              {files.map((file) => (
                <li key={file.id} className="workspace-file-item">
                  <button type="button" className="workspace-file-preview-btn" onClick={() => openFilePreview(file)}>
                    {file.type === 'image' ? (
                      <img src={file.url} alt={file.name} className="workspace-file-thumb" />
                    ) : file.type === 'video' ? (
                      <span className="workspace-file-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                          <rect x="2" y="6" width="14" height="12" rx="2" />
                          <path d="M22 8.5l-6 3.5 6 3.5v-7z" />
                        </svg>
                      </span>
                    ) : (
                      <span className="workspace-file-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <path d="M14 2v6h6" />
                        </svg>
                      </span>
                    )}
                  </button>
                  <div className="workspace-file-body">
                    <span className="workspace-file-name">{file.name}</span>
                    <span className="workspace-file-uploader">{memberDisplayName({ email: file.uploadedByName })}</span>
                  </div>
                  {renderFileActions(file)}
                  {(file.uploadedByUid === user?.uid || isAdmin) && (
                    <button
                      type="button"
                      className="workspace-file-remove-btn"
                      onClick={() => handleDeleteFile(file)}
                      aria-label={t('deleteFileAria')}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 16, height: 16 }}>
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {isAdmin && (
            <form className="workspace-invite-form" onSubmit={handleInvite}>
              <h3>{t('inviteByEmail')}</h3>
              <input
                className="workspace-input"
                type="email"
                placeholder={t('inviteEmailPlaceholder')}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={inviting}
              />
              <button className="workspace-submit-btn" type="submit" disabled={inviting || !inviteEmail.trim()}>
                {t('invite')}
              </button>
              {inviteNotice && <p className="workspace-invite-notice">{inviteNotice}</p>}
              {inviteError && <p className="workspace-error">{inviteError}</p>}
            </form>
          )}

          {isAdmin && (
            <div className="workspace-danger-zone">
              <button
                type="button"
                className="workspace-danger-btn"
                onClick={handleDeleteWorkspace}
                disabled={deletingWorkspace}
              >
                {deletingWorkspace ? t('deleting') : t('deleteWorkspace')}
              </button>
              {deleteWorkspaceError && <p className="workspace-error">{deleteWorkspaceError}</p>}
            </div>
          )}
        </aside>

        <section className={`workspace-chat${sidebarOpen ? ' sidebar-open-mobile' : ''}`}>
          <div className="workspace-chat-window">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`workspace-message workspace-message-${message.role}${message.authorUid === user?.uid ? ' own' : ''}`}
              >
                <span className="workspace-message-author">
                  {message.role === 'ai' ? message.authorName : (message.authorUid === user?.uid ? t('youBadge') : message.authorName)}
                </span>
                {message.attachments && message.attachments.length > 0 && (
                  <div className="message-attachments">
                    {message.attachments.map((a, idx) => (
                      <div key={idx} className="message-attachment">
                        {a.type === 'video' ? (
                          <video src={a.url} controls className="message-attachment-media" />
                        ) : a.type === 'document' ? (
                          <a
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            className="message-attachment-document"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                              <path d="M14 2v6h6" />
                            </svg>
                            <span>{a.name}</span>
                          </a>
                        ) : (
                          <img
                            src={a.url}
                            alt={a.name || 'attachment'}
                            className="message-attachment-media"
                            onClick={() => setPreviewFile(a)}
                          />
                        )}
                        {renderFileActions(a)}
                      </div>
                    ))}
                  </div>
                )}
                <p>{message.text}</p>
                {message.assignedToName && (
                  <span className="workspace-task-badge">{t('taskAssignedBadgePrefix')} {message.assignedToName}</span>
                )}
                {members.length > 0 && (
                  <select
                    className="workspace-task-assign-select"
                    value=""
                    onChange={(e) => {
                      const targetUid = e.target.value;
                      e.target.value = '';
                      if (targetUid) handleAssignTask(message, targetUid);
                    }}
                  >
                    <option value="" disabled>{t('assignTaskPlaceholder')}</option>
                    {members.map((member) => (
                      <option key={member.uid} value={member.uid}>{memberDisplayName(member)}</option>
                    ))}
                  </select>
                )}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {sendError && <p className="workspace-error">{sendError}</p>}

          {pendingAttachments.length > 0 && (
            <div className="input-attachments-preview">
              {pendingAttachments.map((a) => (
                <div key={a.id} className={`input-attachment-thumb${a.kind === 'document' ? ' document' : ''}`}>
                  {a.kind === 'video' ? (
                    <video src={a.previewUrl} className="input-attachment-thumb-media" />
                  ) : a.kind === 'document' ? (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 20, height: 20 }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                      </svg>
                      <span className="input-attachment-thumb-name">{a.file.name}</span>
                    </>
                  ) : (
                    <img src={a.previewUrl} alt={a.file.name} className="input-attachment-thumb-media" />
                  )}
                  <button
                    type="button"
                    className="input-attachment-remove-btn"
                    onClick={() => removePendingAttachment(a.id)}
                    aria-label={t('removeAttachmentAria')}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 18, height: 18 }}>
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className={`screen-share-preview${screenShareActive ? '' : ' hidden'}`}>
            <video ref={screenVideoRef} muted playsInline className="screen-share-preview-video" />
            <span className="screen-share-badge">{t('screenSharing')}</span>
            <button
              type="button"
              className="screen-share-stop-btn"
              onClick={toggleScreenShare}
              aria-label={t('stopScreenShareAria')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 18, height: 18 }}>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <form className="input-area workspace-chat-input" onSubmit={handleSend}>
            <div className="input-shell">
              <input type="file" accept="image/*" multiple hidden ref={imageInputRef} onChange={(e) => handleAttachFiles(e, 'image')} />
              <input type="file" accept="video/*" multiple hidden ref={videoInputRef} onChange={(e) => handleAttachFiles(e, 'video')} />
              <input type="file" accept={DOCUMENT_ACCEPT} multiple hidden ref={documentInputRef} onChange={(e) => handleAttachFiles(e, 'document')} />
              <div className="attach-menu-wrapper" ref={attachMenuRef}>
                <button
                  type="button"
                  className="composer-icon-btn"
                  onClick={() => setAttachMenuOpen((open) => !open)}
                  disabled={sending}
                  aria-label={t('attachFileAria')}
                  aria-expanded={attachMenuOpen}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                  </svg>
                </button>
                {attachMenuOpen && (
                  <div className="attach-menu">
                    <button
                      type="button"
                      className="attach-menu-item"
                      onClick={() => { setAttachMenuOpen(false); imageInputRef.current?.click(); }}
                      disabled={pendingAttachments.filter((a) => a.kind !== 'document').length >= MAX_IMAGE_ATTACHMENTS}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <path d="M21 15l-5-5L5 21" />
                      </svg>
                      {t('attachImages')}
                    </button>
                    <button
                      type="button"
                      className="attach-menu-item"
                      onClick={() => { setAttachMenuOpen(false); videoInputRef.current?.click(); }}
                      disabled={pendingAttachments.filter((a) => a.kind !== 'document').length >= MAX_IMAGE_ATTACHMENTS}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                        <rect x="2" y="6" width="14" height="12" rx="2" />
                        <path d="M22 8.5l-6 3.5 6 3.5v-7z" />
                      </svg>
                      {t('attachVideos')}
                    </button>
                    <button
                      type="button"
                      className="attach-menu-item"
                      onClick={() => { setAttachMenuOpen(false); documentInputRef.current?.click(); }}
                      disabled={pendingAttachments.filter((a) => a.kind === 'document').length >= MAX_DOCUMENT_ATTACHMENTS}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                      </svg>
                      {t('attachFiles')}
                    </button>
                  </div>
                )}
              </div>
              {screenShareSupported && (
                <button
                  type="button"
                  className={`composer-icon-btn${screenShareActive ? ' active' : ''}`}
                  onClick={toggleScreenShare}
                  disabled={sending}
                  aria-label={t('screenShareAria')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 18, height: 18 }}>
                    <rect x="2" y="4" width="20" height="14" rx="2" />
                    <line x1="8" y1="21" x2="16" y2="21" />
                    <line x1="12" y1="17" x2="12" y2="21" />
                    <polyline points="9 10 12 7 15 10" />
                    <line x1="12" y1="7" x2="12" y2="14" />
                  </svg>
                </button>
              )}
              <input
                type="text"
                placeholder={t('workspaceChatPlaceholder')}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={sending}
              />
              <button
                className="composer-send-btn"
                type="submit"
                disabled={sending || (!draft.trim() && pendingAttachments.length === 0 && !screenShareActive)}
                aria-label={t('sendLabel')}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18 }}>
                  <path d="M3.4 20.4l17.45-7.48c.98-.42.98-1.82 0-2.24L3.4 3.2c-.88-.38-1.79.44-1.52 1.35l1.7 5.63a1 1 0 00.95.71h8.03l-8.03.86a1 1 0 00-.93.72l-1.72 6.53c-.24.9.66 1.69 1.52 1.32z"/>
                </svg>
              </button>
            </div>
          </form>
        </section>
      </main>

      {previewFile && (
        <div className="file-lightbox-overlay" onClick={() => setPreviewFile(null)}>
          <div className="file-lightbox" onClick={(e) => e.stopPropagation()}>
            {previewFile.type === 'video' ? (
              <video src={previewFile.url} controls autoPlay className="file-lightbox-media" />
            ) : (
              <img src={previewFile.url} alt={previewFile.name || ''} className="file-lightbox-media" />
            )}
            <div className="file-lightbox-footer">
              <span className="file-lightbox-name">{previewFile.name}</span>
              {renderFileActions(previewFile)}
            </div>
            <button
              type="button"
              className="file-lightbox-close"
              onClick={() => setPreviewFile(null)}
              aria-label={t('closeMenuAria')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 20, height: 20 }}>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
